/**
 * Unified Objednávky list — PRODUCT payments + fulfillment orders as one card model.
 * Nákup ≡ Objednávka; pickup / receipt / buy-again are card-level, not separate tabs.
 */
import type { LogoChipBackgroundSettings, LogoChipRimSettings } from 'pi-kiosk-shared';
import {
  isCurrencyCode,
  normalizeLogoChipBackgroundSettings,
  normalizeLogoChipRimSettings,
} from 'pi-kiosk-shared';
import type { CustomerOrderListItem } from '../../orders/ordersApi.js';
import { usableAccountQrTenant } from '../../../shared/tenant/customerApiTenant.js';
import type {
  CustomerMembershipItemDTO,
  PaymentHistoryItemDTO,
  ProductCollectionMode,
} from '../accountTypes.js';
import {
  isAwaitingQrPayment,
  isCancellablePendingPayment,
} from './accountPaymentsPresenter.js';

/** Hub QR drill-in — never encode empty / `__platform__` / reserved as `?tenant=`. */
function buildAccountPaymentQrHref(
  accountBase: string,
  paymentId: string,
  tenantCandidate: string,
): string {
  const path = `${accountBase}/payments/${encodeURIComponent(paymentId)}/qr`;
  const usable = usableAccountQrTenant(tenantCandidate);
  return usable !== null
    ? `${path}?tenant=${encodeURIComponent(usable)}`
    : path;
}

export type UnifiedOrdersScreenPhase = 'loading' | 'error' | 'empty' | 'populated';

/** Branding join from memberships + public customer-tenants (logo + rim + background) keyed by tenantCode. */
export interface UnifiedOrderTenantBranding {
  readonly tenantId: number;
  readonly tenantLogoUrl: string | null;
  readonly tenantLogoUrlDark: string | null;
  readonly logoChipRim: LogoChipRimSettings;
  readonly logoChipBackground: LogoChipBackgroundSettings;
}

/**
 * Minimal public-directory row for orders branding fallback (`/public/customer-tenants`).
 * Same square `logoUrl` + `logoUrlDark` + rim + background fields as `/orgs` / TenantSelect.
 */
export interface UnifiedOrderPublicTenantBrandingSource
  extends LogoChipRimSettings, LogoChipBackgroundSettings {
  readonly tenantId: number;
  readonly code: string;
  readonly logoUrl: string | null;
  readonly logoUrlDark: string | null;
}

/**
 * Build tenant branding map: memberships win when present; public customer-tenants
 * fill codes with no membership row (guest orders / memberships query empty-failed).
 */
export function buildUnifiedOrderTenantBrandingByCode(
  memberships: readonly CustomerMembershipItemDTO[],
  publicTenants: readonly UnifiedOrderPublicTenantBrandingSource[] = [],
): ReadonlyMap<string, UnifiedOrderTenantBranding> {
  const map = new Map<string, UnifiedOrderTenantBranding>();
  for (const row of memberships) {
    const code = row.tenantCode.trim();
    if (code.length === 0 || map.has(code)) {
      continue;
    }
    map.set(code, {
      tenantId: row.tenantId,
      tenantLogoUrl: row.logoUrl,
      tenantLogoUrlDark: row.logoUrlDark,
      logoChipRim: normalizeLogoChipRimSettings(row),
      logoChipBackground: normalizeLogoChipBackgroundSettings(row),
    });
  }
  for (const row of publicTenants) {
    const code = row.code.trim();
    if (code.length === 0 || map.has(code)) {
      continue;
    }
    map.set(code, {
      tenantId: row.tenantId,
      tenantLogoUrl: row.logoUrl,
      tenantLogoUrlDark: row.logoUrlDark,
      logoChipRim: normalizeLogoChipRimSettings(row),
      logoChipBackground: normalizeLogoChipBackgroundSettings(row),
    });
  }
  return map;
}

function resolveCardBranding(
  tenantCode: string,
  knownTenantId: number | null,
  brandingByCode: ReadonlyMap<string, UnifiedOrderTenantBranding> | undefined,
): {
  readonly tenantId: number | null;
  readonly tenantLogoUrl: string | null;
  readonly tenantLogoUrlDark: string | null;
  readonly logoChipRim: LogoChipRimSettings;
  readonly logoChipBackground: LogoChipBackgroundSettings;
} {
  const fromBranding = brandingByCode?.get(tenantCode);
  const tenantId =
    knownTenantId !== null && knownTenantId > 0
      ? knownTenantId
      : (fromBranding?.tenantId ?? null);
  return {
    tenantId,
    tenantLogoUrl: fromBranding?.tenantLogoUrl ?? null,
    tenantLogoUrlDark: fromBranding?.tenantLogoUrlDark ?? null,
    logoChipRim: fromBranding?.logoChipRim ?? normalizeLogoChipRimSettings(null),
    logoChipBackground:
      fromBranding?.logoChipBackground ?? normalizeLogoChipBackgroundSettings(null),
  };
}

export interface UnifiedOrderCard {
  readonly key: string;
  readonly transactionId: number;
  readonly paymentId: string | null;
  /** Owning org id when known (payment, order wire G2, or branding join). */
  readonly tenantId: number | null;
  readonly tenantCode: string;
  readonly tenantName: string;
  /** Square logo from memberships or public tenants; null → monogram. */
  readonly tenantLogoUrl: string | null;
  readonly tenantLogoUrlDark: string | null;
  readonly logoChipRim: LogoChipRimSettings;
  readonly logoChipBackground: LogoChipBackgroundSettings;
  readonly amountMinor: number | null;
  readonly currency: string | null;
  readonly createdAt: string | null;
  /** Payment status when a payment row exists; otherwise order status. */
  readonly status: string;
  readonly paymentStatus: string | null;
  readonly orderStatus: string | null;
  readonly hasFulfillment: boolean;
  readonly collectTiming: 'NOW' | 'LATER' | null;
  readonly pickupPointName: string | null;
  readonly promisedPickupAt: string | null;
  readonly productCollectionMode: ProductCollectionMode | null;
  readonly paymentRequired: boolean;
  readonly canBuyAgain: boolean;
  readonly isAwaitingQr: boolean;
  readonly isCancellable: boolean;
  readonly receipt: PaymentHistoryItemDTO | null;
  readonly detailHref: string | null;
  readonly qrHref: string | null;
}

function paymentSortTime(item: PaymentHistoryItemDTO): number {
  return Date.parse(item.createdAt) || 0;
}

function orderSortTime(order: CustomerOrderListItem): number {
  if (order.promisedPickupAt) {
    const t = Date.parse(order.promisedPickupAt);
    if (Number.isFinite(t)) {
      return t;
    }
  }
  return order.transactionId;
}

/**
 * Shared buy-again CTA gate for list cards + order detail.
 * Hide when: !usableShop | buyAgainEligible!==true | paymentRequired | non-COMPLETED paymentStatus
 * | missing/invalid currency (no silent CZK invent).
 * Omitted/null paymentStatus does not hide by itself (paymentRequired covers unpaid when status absent).
 */
export function resolveCanBuyAgain(input: {
  readonly usableShop: boolean;
  readonly paymentStatus: string | null | undefined;
  readonly paymentRequired: boolean | undefined;
  readonly buyAgainEligible: boolean | undefined;
  /** Prior order charge currency — omit/invalid → hide (G8 fail-closed). */
  readonly currency?: string | null;
}): boolean {
  if (!input.usableShop) {
    return false;
  }
  // G2 fail-closed: omit/false → hide (no legacy COMPLETED / paymentRequired===false).
  if (input.buyAgainEligible !== true) {
    return false;
  }
  // G3: unpaid / payment still required → hide even when BE gate is stale true.
  if (input.paymentRequired === true) {
    return false;
  }
  const paymentStatus = input.paymentStatus ?? null;
  if (paymentStatus !== null && paymentStatus !== 'COMPLETED') {
    return false;
  }
  // G8: no buy-again without an explicit prior currency (never invent CZK).
  if (!isCurrencyCode(input.currency)) {
    return false;
  }
  return true;
}

export function buildUnifiedOrderCards(input: {
  readonly payments: readonly PaymentHistoryItemDTO[];
  readonly orders: readonly CustomerOrderListItem[];
  /** Tenant used for order-only rows when payment/order tenant is unknown (rare). */
  readonly fallbackTenantCode: string;
  /** Optional hub base; defaults to Wave D `/account`. */
  readonly accountBase?: string;
  /** Memberships + public-tenants join for square logo + rim + order-only tenantId. */
  readonly brandingByTenantCode?: ReadonlyMap<string, UnifiedOrderTenantBranding>;
}): UnifiedOrderCard[] {
  const accountBase = input.accountBase ?? '/account';
  const brandingByCode = input.brandingByTenantCode;
  const orderByTx = new Map<number, CustomerOrderListItem>();
  for (const order of input.orders) {
    orderByTx.set(order.transactionId, order);
  }

  const seenTx = new Set<number>();
  const cards: UnifiedOrderCard[] = [];

  const productPayments = input.payments.filter((p) => p.purposeType === 'PRODUCT');

  for (const payment of productPayments) {
    seenTx.add(payment.transactionId);
    const order = orderByTx.get(payment.transactionId) ?? null;
    const hasFulfillment = order !== null;
    // Row tenant for drill-in (payment tag, else order fan-out tag). BE G7
    // guarantees non-empty shop slug; trim for whitespace-defensive FE.
    const paymentTenant = payment.tenantCode.trim();
    const orderTenant = (order?.tenantCode ?? '').trim();
    let tenantCode = input.fallbackTenantCode;
    if (paymentTenant.length > 0) {
      tenantCode = paymentTenant;
    } else if (orderTenant.length > 0) {
      tenantCode = orderTenant;
    }
    const paymentName = payment.tenantName.trim();
    const tenantName =
      paymentName.length > 0 ? paymentName : (order?.tenantName ?? '');
    // Prefer payment.tenantId; else order wire tenantId (G2); else membership join.
    let knownTenantId: number | null = null;
    if (payment.tenantId > 0) {
      knownTenantId = payment.tenantId;
    } else if (typeof order?.tenantId === 'number' && order.tenantId > 0) {
      knownTenantId = order.tenantId;
    }
    const branding = resolveCardBranding(tenantCode, knownTenantId, brandingByCode);
    const detailHref = hasFulfillment
      ? `${accountBase}/orders/${payment.transactionId}`
      : null;
    const awaitingQr = isAwaitingQrPayment(payment);
    // Prefer real org slug for QR query (skip empty / platform / reserved at each hop).
    const qrTenantCandidate =
      usableAccountQrTenant(paymentTenant) ??
      usableAccountQrTenant(orderTenant) ??
      input.fallbackTenantCode;
    cards.push({
      key: `pay-${payment.paymentId}`,
      transactionId: payment.transactionId,
      paymentId: payment.paymentId,
      tenantId: branding.tenantId,
      tenantCode,
      tenantName,
      tenantLogoUrl: branding.tenantLogoUrl,
      tenantLogoUrlDark: branding.tenantLogoUrlDark,
      logoChipRim: branding.logoChipRim,
      logoChipBackground: branding.logoChipBackground,
      amountMinor: payment.amountMinor,
      currency: payment.currency,
      createdAt: payment.createdAt,
      status: payment.status,
      paymentStatus: payment.status,
      orderStatus: order?.status ?? null,
      hasFulfillment,
      collectTiming: order?.collectTiming ?? null,
      pickupPointName: order?.pickupPointName ?? null,
      promisedPickupAt: order?.promisedPickupAt ?? null,
      productCollectionMode:
        payment.productCollectionMode ?? order?.productCollectionMode ?? null,
      paymentRequired: order?.paymentRequired === true,
      // Hide CTA unless BE gate + paid COMPLETED (fail-closed when flag omitted).
      canBuyAgain: resolveCanBuyAgain({
        usableShop: usableAccountQrTenant(tenantCode) !== null,
        paymentStatus: payment.status,
        paymentRequired: order?.paymentRequired,
        buyAgainEligible: order?.buyAgainEligible ?? payment.buyAgainEligible,
        currency: payment.currency,
      }),
      isAwaitingQr: awaitingQr,
      isCancellable: isCancellablePendingPayment(payment),
      receipt: payment,
      detailHref,
      qrHref: awaitingQr
        ? buildAccountPaymentQrHref(accountBase, payment.paymentId, qrTenantCandidate)
        : null,
    });
  }

  for (const order of input.orders) {
    if (seenTx.has(order.transactionId)) {
      continue;
    }
    const orderTenant = (order.tenantCode ?? '').trim();
    const tenantCode =
      orderTenant.length > 0 ? orderTenant : input.fallbackTenantCode;
    const tenantName = order.tenantName ?? '';
    const orderTenantId =
      typeof order.tenantId === 'number' && order.tenantId > 0 ? order.tenantId : null;
    const branding = resolveCardBranding(tenantCode, orderTenantId, brandingByCode);
    cards.push({
      key: `ord-${order.transactionId}`,
      transactionId: order.transactionId,
      paymentId: null,
      tenantId: branding.tenantId,
      tenantCode,
      tenantName,
      tenantLogoUrl: branding.tenantLogoUrl,
      tenantLogoUrlDark: branding.tenantLogoUrlDark,
      logoChipRim: branding.logoChipRim,
      logoChipBackground: branding.logoChipBackground,
      amountMinor: order.amountMinor,
      currency: order.currency ?? null,
      createdAt: order.createdAt ?? null,
      status: order.status,
      paymentStatus: null,
      orderStatus: order.status,
      hasFulfillment: true,
      collectTiming: order.collectTiming ?? null,
      pickupPointName: order.pickupPointName ?? null,
      promisedPickupAt: order.promisedPickupAt ?? null,
      productCollectionMode: order.productCollectionMode ?? null,
      paymentRequired: order.paymentRequired === true,
      canBuyAgain: resolveCanBuyAgain({
        usableShop: usableAccountQrTenant(tenantCode) !== null,
        paymentStatus: null,
        paymentRequired: order.paymentRequired,
        buyAgainEligible: order.buyAgainEligible,
        currency: order.currency ?? null,
      }),
      isAwaitingQr: false,
      isCancellable: false,
      receipt: null,
      detailHref: `${accountBase}/orders/${order.transactionId}`,
      qrHref: null,
    });
  }

  cards.sort((a, b) => {
    const aPay = a.receipt ? paymentSortTime(a.receipt) : 0;
    const bPay = b.receipt ? paymentSortTime(b.receipt) : 0;
    if (aPay !== 0 || bPay !== 0) {
      return bPay - aPay;
    }
    const aOrd = orderByTx.get(a.transactionId);
    const bOrd = orderByTx.get(b.transactionId);
    return (bOrd ? orderSortTime(bOrd) : b.transactionId) - (aOrd ? orderSortTime(aOrd) : a.transactionId);
  });

  return cards;
}

export function splitPendingAndHistory(
  cards: readonly UnifiedOrderCard[],
): {
  readonly pending: UnifiedOrderCard[];
  readonly history: UnifiedOrderCard[];
} {
  const pending: UnifiedOrderCard[] = [];
  const history: UnifiedOrderCard[] = [];
  for (const card of cards) {
    if (card.isCancellable || card.isAwaitingQr) {
      pending.push(card);
    } else {
      history.push(card);
    }
  }
  return { pending, history };
}

export function resolveUnifiedOrdersPhase(input: {
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly cardCount: number;
}): UnifiedOrdersScreenPhase {
  if (input.isLoading) {
    return 'loading';
  }
  if (input.isError) {
    return 'error';
  }
  if (input.cardCount === 0) {
    return 'empty';
  }
  return 'populated';
}

export function collectionModeLabelKey(
  mode: ProductCollectionMode | null | undefined,
): string | null {
  if (mode === 'PREPAY_COLLECT_LATER') {
    return 'account.orders.modeCollectLater';
  }
  if (mode === 'PAY_AT_KIOSK') {
    return 'account.orders.modePayAtKiosk';
  }
  return null;
}
